import { InputText } from 'primereact/inputtext';
import { Controller } from 'react-hook-form';
import { logger } from '@/core/logging/logger';
import type { Control } from 'react-hook-form';
import type { TemplateField } from '../../../overview/models/template.types';
import {
  cleanHelperText,
  fieldControlKind,
  formKey,
  isFullWidth,
  isKnownFieldType,
  optionsOf,
} from '../../models/templateRuntime';
import type { TemplateFormValues } from '../../models/templateRuntime';
import { TemplateFieldInput } from './TemplateFieldInput';
import type { TemplateDateStyle } from './TemplateFieldInput';
import type { TemplateFileFormats } from '../../models/templateFile';

interface TemplateFieldControlProps {
  sectionKey: string;
  field: TemplateField;
  control: Control<TemplateFormValues>;
  /** Every control inert: while a submit is in flight, and on a submitted form's View. */
  disabled?: boolean;
  /** Purchase Requests draw a Date as Organization Setup does; every other form as the masters do. */
  dateStyle?: TemplateDateStyle;
  /** A Date's earliest pickable day (Purchase Requests' Delivery Date: today). */
  minDate?: Date;
  /**
   * Whether the cell spans the grid's row. Absent, the type decides (Long text and Address block do); the Supplier
   * master's form keeps its Address blocks to one column (2026-10-08).
   */
  fullWidth?: boolean;
  /** The box's placeholder in place of the type's own (`Enter <label>`, `Select date`): Purchase Requests' (design16). */
  placeholder?: string;
  /** A File upload's allowed formats: Purchase Requests' (2026-10-28); absent, any format. */
  fileFormats?: TemplateFileFormats;
}

/** A DOM id from a form key: dots and spaces are legal in RHF paths, not in a readable id. */
function domId(key: string): string {
  return `template-field-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

/**
 * One configured field, drawn as the control its TYPE -- and, for a choice type,
 * its configured OPTIONS -- imply.
 *
 * One cell of the masters' record-form grid: `ms-field`, its `ms-field__label`
 * with the `ms-field__required` marker, and its `ms-field__error` -- the markup
 * `MasterFieldControl` renders, so the two forms are the same form to the eye.
 * That control is not imported: it is keyed on a master descriptor (option
 * sources, dial-code prefixes, a status list) a template field does not have.
 *
 * Driven entirely by the field. Nothing here knows which template it belongs to,
 * and nothing branches on a field's name.
 */
export function TemplateFieldControl({
  sectionKey,
  field,
  control,
  disabled = false,
  dateStyle,
  minDate,
  fullWidth,
  placeholder,
  fileFormats,
}: TemplateFieldControlProps) {
  const key = formKey(sectionKey, field.fieldKey);
  const id = domId(key);
  const labelId = `${id}-label`;
  const kind = fieldControlKind(field);
  const options = optionsOf(field);
  const helper = cleanHelperText(field.helperText);
  const helperId = helper ? `${id}-help` : undefined;
  const errorId = `${id}-error`;

  if (import.meta.env.DEV && !isKnownFieldType(field.fieldType)) {
    logger.warn(`Unknown template field type "${field.fieldType}"; drawn as text.`);
  }

  const marker =
    field.mandatory && kind !== 'file' && kind !== 'calculated' ? (
      <span className="ms-field__required" aria-hidden="true">
        {' '}
        *
      </span>
    ) : null;

  /*
    A radio group has no single control a <label for> can point at, so it is
    named by aria-labelledby instead. A checkbox carries its label beside the box,
    so it has none above it.
  */
  const label =
    kind === 'checkbox' ? null : kind === 'radio' ? (
      <span id={labelId} className="ms-field__label">
        {field.label}
        {marker}
      </span>
    ) : (
      <label id={labelId} className="ms-field__label" htmlFor={id}>
        {field.label}
        {marker}
      </label>
    );

  const helperLine = helper ? (
    <small id={helperId} className="template-form__help">
      {helper}
    </small>
  ) : null;

  const className = `ms-field${(fullWidth ?? isFullWidth(field.fieldType)) ? ' ms-field--full' : ''}`;

  /* Not submittable: shown, so the form matches the template, but never sent. */
  if (kind === 'file') {
    return (
      <div className={className}>
        {label}
        <p id={id} className="ms-pop__note m-0" aria-disabled="true" aria-describedby={helperId}>
          <i className="pi pi-info-circle" aria-hidden="true" /> File upload is not available yet
        </p>
        {helperLine}
      </div>
    );
  }

  if (kind === 'calculated') {
    return (
      <div className={className}>
        {label}
        <InputText id={id} value="" readOnly disabled placeholder="Calculated automatically" aria-describedby={helperId} />
        {helperLine}
      </div>
    );
  }

  return (
    <div className={className}>
      {label}
      <Controller
        control={control}
        name={key}
        render={({ field: rhf, fieldState }) => {
          const invalid = fieldState.error !== undefined;
          const describedBy = [invalid ? errorId : null, helperId].filter(Boolean).join(' ') || undefined;
          return (
            <>
              <TemplateFieldInput
                kind={kind}
                id={id}
                labelId={labelId}
                field={field}
                options={options}
                rhf={rhf}
                invalid={invalid}
                describedBy={describedBy}
                disabled={disabled}
                marker={marker}
                dateStyle={dateStyle}
                minDate={minDate}
                placeholder={placeholder}
                fileFormats={fileFormats}
              />
              {fieldState.error?.message ? (
                <small id={errorId} className="ms-field__error" role="alert">
                  {fieldState.error.message}
                </small>
              ) : null}
            </>
          );
        }}
      />
      {helperLine}
    </div>
  );
}
